import { API_BASE } from "./api";

const REPORT_GET_CACHE_PREFIX = "zeqi:reports:get:";
const REPORT_GET_CACHE_VERSION = "v1";
const REPORT_CACHE_TTL_MS = 3 * 60 * 1000;
const REPORT_DETAIL_CACHE_TTL_MS = 5 * 60 * 1000;
const ANALYSIS_RECOVERY_TIMEOUT_MS = 2 * 60 * 1000;
const ANALYSIS_RECOVERY_INTERVAL_MS = 3 * 1000;
const reportMemoryCache = new Map();
const inflightReportRequests = new Map();

const reportNow = () => Date.now();
const reportOpenLog = (event, payload = {}) => {
  console.log(`[report-open][frontend] ${event}`, payload);
};

const buildReportCacheKey = (scope, parts = []) =>
  [REPORT_GET_CACHE_PREFIX, REPORT_GET_CACHE_VERSION, scope, ...parts.map((part) => String(part ?? ""))].join(":");

const readReportCache = (key) => {
  const memEntry = reportMemoryCache.get(key);
  if (memEntry && memEntry.expiresAt > reportNow()) {
    return memEntry.value;
  }
  if (memEntry) {
    reportMemoryCache.delete(key);
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.expiresAt <= reportNow()) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    reportMemoryCache.set(key, parsed);
    return parsed.value;
  } catch {
    return null;
  }
};

const writeReportCache = (key, value, ttlMs) => {
  const entry = { value, expiresAt: reportNow() + ttlMs };
  reportMemoryCache.set(key, entry);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore storage failures
  }
  return value;
};

const clearReportCacheKeys = (predicate) => {
  for (const key of reportMemoryCache.keys()) {
    if (predicate(key)) {
      reportMemoryCache.delete(key);
    }
  }
  try {
    const toRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && predicate(key)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore storage failures
  }
};

const getCachedReportJson = async ({ key, ttlMs = REPORT_CACHE_TTL_MS, loader, shouldCache = () => true }) => {
  const cached = readReportCache(key);
  if (cached !== null && cached !== undefined) {
    return cached;
  }

  const inflight = inflightReportRequests.get(key);
  if (inflight) {
    return inflight;
  }

  const request = Promise.resolve()
    .then(loader)
    .then((value) => {
      if (shouldCache(value)) {
        return writeReportCache(key, value, ttlMs);
      }
      return value;
    })
    .finally(() => {
      inflightReportRequests.delete(key);
    });

  inflightReportRequests.set(key, request);
  return request;
};

const hasValidReportId = (parsed) =>
  Boolean(parsed?.report?._id || parsed?.report?.id);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hasAssessmentPayload = (value) =>
  Boolean(value?.assessment);

const isLikelyFetchDisconnect = (error) => {
  const message = String(error?.message || error || "");
  return /failed to fetch|networkerror|load failed|network request failed|network changed/i.test(message);
};

const fetchWithRetry = async (url, options, { retries = 2, retryDelayMs = 350 } = {}) => {
  let attempt = 0;
  let lastError = null;

  while (attempt <= retries) {
    try {
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isLikelyFetchDisconnect(error)) {
        throw error;
      }
      await wait(retryDelayMs * (attempt + 1));
      attempt += 1;
    }
  }

  throw lastError || new Error("Request failed");
};

const NO_STORE_REPORT_PATHS = new Set([
  "/reports/homepage-assessment",
  "/reports/social-media",
]);

const recoverAnalysisAfterDisconnect = async ({
  loadSavedAnalysis,
  timeoutMs = ANALYSIS_RECOVERY_TIMEOUT_MS,
  intervalMs = ANALYSIS_RECOVERY_INTERVAL_MS,
}) => {
  const startedAt = reportNow();
  let lastError = null;

  while (reportNow() - startedAt < timeoutMs) {
    try {
      const result = await loadSavedAnalysis();
      if (hasAssessmentPayload(result)) {
        return result;
      }
    } catch (error) {
      lastError = error;
    }

    await wait(intervalMs);
  }

  if (lastError) throw lastError;
  throw new Error("The analysis completed on the server but could not be retrieved yet.");
};

export const clearMetaReportCaches = ({ userId, shopId } = {}) => {
  const uid = String(userId || "");
  const sid = String(shopId || "");
  const isMetaKey = (k) => k.includes("/reports/meta");
  clearReportCacheKeys((key) => key.includes(uid) && isMetaKey(key));
  void sid;
};

// ─── Dedicated per-report-type fetchers ───────────────────────────────────
// Each function hits its own dedicated route — no collection names needed on client.

async function fetchReport(path, { userId, shopId, authToken, traceId, query = null } = {}) {
  if (!userId) throw new Error("userId is required");
  const url = new URL(`${API_BASE}${path}`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  if (query && typeof query === "object") {
    Object.entries(query).forEach(([key, value]) => {
      if (value == null || value === "") return;
      url.searchParams.set(key, String(value));
    });
  }
  const queryCacheFragment =
    query && typeof query === "object"
      ? Object.entries(query)
          .filter(([, value]) => value != null && value !== "")
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([key, value]) => `${key}=${String(value)}`)
          .join("&")
      : "";
  const cacheKey = buildReportCacheKey("dedicated", [path, userId, shopId || "", queryCacheFragment]);
  const startedAt = reportNow();
  const cached = readReportCache(cacheKey);
  if (cached !== null && cached !== undefined) {
    reportOpenLog("fetchReport.cache_hit", {
      path,
      traceId: traceId || null,
      userId,
      shopId: shopId || null,
      durationMs: reportNow() - startedAt,
    });
    return cached;
  }

  reportOpenLog("fetchReport.start", {
    path,
    traceId: traceId || null,
    userId,
    shopId: shopId || null,
  });

  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_CACHE_TTL_MS,
    shouldCache: hasValidReportId,
    loader: async () => {
      const requestStartedAt = reportNow();
      const shouldNoStore = NO_STORE_REPORT_PATHS.has(path);
      const res = await fetchWithRetry(url.toString(), {
        cache: shouldNoStore ? "no-store" : "default",
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : "",
          ...(shouldNoStore
            ? { "Cache-Control": "no-cache", Pragma: "no-cache" }
            : {}),
          ...(traceId ? { "x-report-open-trace": traceId } : {}),
        },
      });
      const text = await res.text();
      if (!res.ok) throw new Error(`fetchReport(${path}) failed (${res.status}): ${text || res.statusText}`);
      try {
        const parsed = JSON.parse(text);


  if (path === "/reports/meta") {
    console.log("[fetchMetaReport][raw parsed response]", {
      reportId: parsed?.report?._id || parsed?.report?.id || null,
      generationKey: parsed?.report?.data?.generation_key || null,
      collectionName: parsed?.report?.data?.collection_name || null,

      hasCampaignPerformanceTotals: !!parsed?.report?.data?.campaign_performance_totals,
      totalsKeys: Object.keys(parsed?.report?.data?.campaign_performance_totals || {}),

      hasRangeHelper: !!parsed?.report?.data?.campaign_performance_totals?.range_helper,
      rangeHelperKeys: Object.keys(
        parsed?.report?.data?.campaign_performance_totals?.range_helper || {}
      ),

      availableWindows:
        parsed?.report?.data?.campaign_performance_totals?.range_helper?.available_windows || null,

      hasByWindow: !!parsed?.report?.data?.campaign_performance_totals?.range_helper?.by_window,

      byWindowKeys: Object.keys(
        parsed?.report?.data?.campaign_performance_totals?.range_helper?.by_window || {}
      ),

      sampleYesterday:
        parsed?.report?.data?.campaign_performance_totals?.range_helper?.by_window?.yesterday || null,
    });
  }

        reportOpenLog("fetchReport.complete", {
          path,
          traceId: traceId || null,
          durationMs: reportNow() - requestStartedAt,
          totalMs: reportNow() - startedAt,
          reportId: parsed?.report?._id || parsed?.report?.id || null,
        });
        return parsed;
      } catch {
        throw new Error(`fetchReport(${path}) bad JSON: ${text}`);
      }
    },
  });
}

export const fetchDailyOverviewReport        = (opts) => fetchReport("/reports/daily-overview",  opts);
export const fetchSalesOrdersRollingReport   = (opts) => fetchReport("/reports/sales-orders-rolling", opts);

export const fetchSalesOrdersRollingDates = async ({ userId, shopId, authToken } = {}) => {
  if (!userId) throw new Error("userId is required");
  const url = new URL(`${API_BASE}/reports/sales-orders-rolling/dates`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  const res = await fetch(url.toString(), {
    headers: { ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
  });
  if (!res.ok) throw new Error(`fetchSalesOrdersRollingDates: ${res.status}`);
  return res.json();
};
const trimReportToRequestedWindow = (response, requestedWindowKey) => {
  const report = response?.report || null;
  const windows = Array.isArray(report?.data?.windows) ? report.data.windows : null;
  if (!report?.data || !windows || !requestedWindowKey) return response;

  const requestedWindows = windows.filter(
    (windowEntry) => String(windowEntry?.key || "") === String(requestedWindowKey)
  );
  if (!requestedWindows.length) return response;
  if (
    requestedWindows.length === windows.length &&
    report.data.default_window_key === requestedWindowKey
  ) return response;

  return {
    ...response,
    report: {
      ...report,
      data: {
        ...report.data,
        default_window_key: requestedWindowKey,
        windows: requestedWindows,
      },
    },
  };
};

export function getConversionReportFromCache(userId, shopId) {
  const cacheKey = buildReportCacheKey("latest-by-collection-v2", [
    "conversion_and_pdp_report",
    userId,
    shopId || "",
    "",
    "",
    "assessment",
    "last_7_days",
    "",
    "",
    "2",
    "summary",
  ]);
  const raw = readReportCache(cacheKey);
  if (!raw) return null;
  return trimReportToRequestedWindow(raw, "last_7_days");
}

export const fetchHomepageAssessmentReport = async (opts = {}) => {
  const query = {
    section: "assessment",
    windowKey: "last_7_days",
    progressiveVersion: "2",
    ...(opts.query || {}),
  };
  const response = await fetchReport("/reports/homepage-assessment", {
    ...opts,
    query,
  });
  return trimReportToRequestedWindow(response, query.windowKey);
};
export const fetchSocialMediaReport          = (opts) => fetchReport("/reports/social-media",  opts);
export const fetchSocialMediaAssessmentReport = async (opts = {}) => {
  const query = {
    section: "assessment",
    windowKey: "last_7_days",
    progressiveVersion: "2",
    ...(opts.query || {}),
  };
  const response = await fetchReport("/reports/social-media", {
    ...opts,
    query,
  });
  return trimReportToRequestedWindow(response, query.windowKey);
};
export const fetchSocialMediaRawReport = (opts = {}) =>
  fetchReport("/reports/social-media", {
    ...opts,
    query: { ...(opts.query || {}), section: "report" },
  });
export const fetchConversionAndPdpReport = async ({
  userId,
  shopId,
  authToken,
  traceId,
} = {}) => {
  return fetchConversionAndPdpAssessmentReport({
    userId,
    shopId,
    authToken,
    traceId,
    windowKey: "last_7_days",
    rankingMode: "core",
  });
};
export const fetchConversionAndPdpAssessmentReport = async ({
  userId,
  shopId,
  authToken,
  startDate,
  endDate,
  windowKey,
  reportId,
  generationKey,
  rankingMode = "core",
} = {}) => {
  if (!userId) throw new Error("userId is required");
  const response = await getLatestReportByCollection({
    collection: "conversion_and_pdp_report",
    userId,
    shopId,
    authToken,
    section: "assessment",
    startDate,
    endDate,
    windowKey,
    reportId,
    generationKey,
    rankingMode,
    progressiveVersion: "2",
  });
  return trimReportToRequestedWindow(response, windowKey);
};

export async function generateConversionAndPdpReport({ userId, shopId, authToken, startDate, endDate } = {}) {
  if (!userId) throw new Error("userId is required");
  const endpoint = `${API_BASE}/google/create-conversion-and-pdp-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ userId, shopId, startDate, endDate }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Conversion & PDP report generation failed (${res.status}): ${text || res.statusText}`);
  }
  return parseJsonSafely(res);
}

export const fetchDailyOverviewDates = async ({ userId, shopId, authToken } = {}) => {
  if (!userId) throw new Error("userId is required");
  const url = new URL(`${API_BASE}/reports/daily-overview/dates`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) throw new Error(`fetchDailyOverviewDates: ${res.status}`);
  return res.json();
};

export const fetchConversionAndPdpWindows = async ({ userId, shopId, authToken } = {}) => {
  if (!userId) throw new Error("userId is required");
  const url = new URL(`${API_BASE}/reports/conversion-and-pdp/windows`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  reportOpenLog("fetchConversionAndPdpWindows.start", {
    userId,
    shopId: shopId || null,
    url: url.toString(),
  });
  const res = await fetch(url.toString(), {
    headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
  });
  if (!res.ok) throw new Error(`fetchConversionAndPdpWindows: ${res.status}`);
  const parsed = await res.json();
  reportOpenLog("fetchConversionAndPdpWindows.complete", {
    userId,
    shopId: shopId || null,
    windowCount: Array.isArray(parsed?.windows) ? parsed.windows.length : null,
    firstWindow: Array.isArray(parsed?.windows) ? parsed.windows[0] || null : null,
  });
  return parsed;
};
export async function getPdpDeepDiveAnalysis({ userId, reportId, shopId, itemId, itemName, shopifyProductId, authToken } = {}) {
  if (!userId) throw new Error("userId is required");
  const params = new URLSearchParams({
    userId: userId || "",
    ...(reportId ? { reportId } : {}),
    ...(shopId ? { shopId } : {}),
    ...(itemId ? { itemId } : {}),
    ...(itemName ? { itemName } : {}),
    ...(shopifyProductId ? { shopifyProductId } : {}),
  });
  const res = await fetch(`${API_BASE}/reports/conversion-and-pdp/pdp-deep-dive?${params.toString()}`, {
    headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
  });
  const json = await res.json();
  if (!res.ok || json?.ok === false) throw new Error(json?.message || "Failed to fetch PDP deep dive analysis");
  return json;
}

export async function analyzePdpDeepDive({ userId, reportId, shopId, itemId, itemName, shopifyProductId, authToken, force = false } = {}) {
  if (!userId) throw new Error("userId is required");
  const res = await fetch(`${API_BASE}/reports/conversion-and-pdp/pdp-deep-dive/analyze`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({
      user_id: userId,
      report_id: reportId,
      shop_id: shopId,
      item_id: itemId,
      item_name: itemName,
      shopify_product_id: shopifyProductId,
      force,
    }),
  });
  const json = await res.json();
  if (!res.ok || json?.ok === false) throw new Error(json?.message || "Failed to analyze PDP deep dive");
  return json;
}

export async function getProductGscPerformance({ shopId, productIds = [], authToken } = {}) {
  if (!shopId || !productIds.length) return {};
  const params = new URLSearchParams({ shop_id: shopId, productIds: productIds.join(",") });
  const res = await fetch(`${API_BASE}/reports/conversion-and-pdp/pdp-gsc-performance?${params}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  const json = await res.json();
  return json?.data || {};
}

export const fetchBrandIntelligenceReport = (opts) => fetchReport("/reports/brand-intelligence", opts);
export const fetchBenchmarkReport      = (opts) => fetchReport("/reports/benchmark",       opts);
export const fetchMetaReport           = (opts) => fetchReport("/reports/meta",            opts);
export const fetchMetaReportSummary    = (opts) => fetchReport("/reports/meta/summary",    opts);
export const fetchMetaOverallReport    = (opts) => fetchReport("/reports/meta/overall",    opts);
export const fetchMetaRawDataReport    = (opts) => fetchReport("/reports/meta/raw",        opts);
export const fetchGscReport            = (opts) => fetchReport("/reports/gsc",             opts);

export async function refreshGscReport({
  userId,
  shopId,
  strategyId,
  authToken,
  forceRefresh = true,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to refresh Google Search Console report");
  }
  if (!shopId) {
    throw new Error("shopId is required to refresh Google Search Console report");
  }
  console.log("[GSC_REFRESH][frontend] POST /reports/gsc/refresh", {
    userId,
    shopId,
    strategyId: strategyId || null,
    forceRefresh,
  });
  const response = await fetch(`${API_BASE}/reports/gsc/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({
      userId,
      shopId,
      strategyId,
      forceRefresh,
    }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.ok === false) {
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to refresh Google Search Console report"
    );
  }

  const refreshedReportId = json?.reportId || json?.report_id || null;
  clearReportCacheKeys((key) =>
    key.includes(String(userId)) &&
    (
      key.includes("/reports/gsc") ||
      key.includes("google_search_console") ||
      (refreshedReportId && key.includes(String(refreshedReportId)))
    )
  );

  try {
    window.dispatchEvent(
      new CustomEvent("zeqi:report-refreshed", {
        detail: {
          collectionName: "google_search_console",
          reportId: refreshedReportId,
          report: json?.report || null,
        },
      })
    );
  } catch {
    // ignore non-browser/test environments
  }

  return json;
}
export const fetchGmcReport            = (opts) => fetchReport("/reports/gmc",             opts);
export const fetchRevenueRealityReport = (opts) => fetchReport("/reports/revenue-reality", opts);
export const fetchMarketReport         = (opts) => fetchReport("/reports/market",          opts);
export const fetchMarketReportV2       = ({ userId, shopId, authToken }) =>
  getLatestReportByCollection({ collection: "shop_market_analysis_v2", userId, shopId, authToken });
export const fetchTargetAudienceReport = (opts) => fetchReport("/reports/target-audience", opts);
export const fetchCompetitorReport     = (opts) => fetchReport("/reports/competitor",      opts);
export const fetchYearlyContentReport  = (opts) => fetchReport("/reports/yearly-content",  opts);
export const fetchIdeationReport       = (opts) => fetchReport("/reports/ideation",        opts);
export const fetchSeoReport            = (opts) => fetchReport("/reports/seo",             opts);
export const fetchAdsAuditReport       = (opts) => fetchReport("/reports/ads-audit",       opts);
export const fetchInfluencerReport     = (opts) => fetchReport("/reports/influencer",      opts);

// ─────────────────────────────────────────────────────────────────────────

const parseJsonSafely = async (res) => {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(`Failed to parse JSON (${res.url || "response"}): ${err.message}`);
  }
};

export async function createShopBenchmarkReport({ userId, authToken } = {}) {
  const endpoint = `${API_BASE}/shop/create-benchmark-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const body = userId ? { user_id: userId } : {};

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Benchmark report request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

/**
 * Single round-trip replacement for getLatestMatchingReport + getReportById.
 * Returns the full report document directly from one backend query.
 */
export async function getLatestReportByCollection({
  collection,
  userId,
  shopId,
  authToken,
  startDate,
  endDate,
  section,
  windowKey,
  reportId,
  generationKey,
  progressiveVersion,
  rankingMode,
} = {}) {
  if (!collection) throw new Error("collection is required");
  if (!userId) throw new Error("userId is required");
  const isConversionAndPdpLookup = collection === "conversion_and_pdp_report";

  const url = new URL(`${API_BASE}/reports/latest/${encodeURIComponent(collection)}`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  if (startDate) url.searchParams.set("startDate", startDate);
  if (endDate) url.searchParams.set("endDate", endDate);
  if (section) url.searchParams.set("section", section);
  if (windowKey) url.searchParams.set("windowKey", windowKey);
  if (reportId) url.searchParams.set("reportId", reportId);
  if (generationKey) url.searchParams.set("generationKey", generationKey);
  if (progressiveVersion) url.searchParams.set("progressiveVersion", progressiveVersion);
  if (rankingMode) url.searchParams.set("rankingMode", rankingMode);
  const cacheKey = buildReportCacheKey("latest-by-collection-v2", [collection, userId, shopId || "", startDate || "", endDate || "", section || "", windowKey || "", reportId || "", generationKey || "", progressiveVersion || "", rankingMode || ""]);
  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_CACHE_TTL_MS,
    shouldCache: hasValidReportId,
    loader: async () => {
      if (isConversionAndPdpLookup) {
        reportOpenLog("getLatestReportByCollection.start", {
          collection,
          userId,
          shopId: shopId || null,
          startDate: startDate || null,
          endDate: endDate || null,
          url: url.toString(),
        });
      }
      const res = await fetch(url.toString(), {
        method: "GET",
        cache: "no-store",
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      });

      const text = await res.text();
      if (!res.ok) throw new Error(`getLatestReportByCollection failed (${res.status}): ${text || res.statusText}`);

      try {
        const parsed = JSON.parse(text);
        if (isConversionAndPdpLookup) {
          reportOpenLog("getLatestReportByCollection.complete", {
            collection,
            userId,
            shopId: shopId || null,
            startDate: startDate || null,
            endDate: endDate || null,
            reportId: parsed?.report?._id || parsed?.report?.id || null,
            generationKey: parsed?.report?.data?.generation_key || null,
            reportingPeriod: parsed?.report?.data?.reporting_period || null,
          });
        }
        return parsed;
      } catch {
        throw new Error(`Failed to parse getLatestReportByCollection response: "${text}"`);
      }
    },
  });
}

export async function getLatestMatchingReport({
  userId,
  shopId,
  collections,
  authToken,
} = {}) {
  if (!userId) throw new Error("userId is required");
  const normalizedCollections = Array.isArray(collections)
    ? collections.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  if (!normalizedCollections.length) {
    throw new Error("collections is required");
  }

  const url = new URL(`${API_BASE}/reports/latest-match`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  url.searchParams.set("collections", normalizedCollections.join(","));
  const cacheKey = buildReportCacheKey("latest-match", [userId, shopId || "", normalizedCollections.join(",")]);
  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_CACHE_TTL_MS,
    loader: async () => {
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : "",
        },
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(
          `Latest matching report request failed (${res.status}): ${text || res.statusText}`
        );
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Failed to parse latest matching report response: "${text}"`);
      }
    },
  });
}

export async function getReportById({ reportId, userId, authToken, traceId } = {}) {
  if (!reportId) throw new Error("reportId is required");
  if (!userId) throw new Error("userId is required");

  const url = new URL(`${API_BASE}/reports/${reportId}`);
  url.searchParams.set("userId", userId);
  const cacheKey = buildReportCacheKey("report-by-id", [reportId, userId]);
  const startedAt = reportNow();
  const cached = readReportCache(cacheKey);
  if (cached !== null && cached !== undefined) {
    reportOpenLog("getReportById.cache_hit", {
      reportId,
      traceId: traceId || null,
      userId,
      durationMs: reportNow() - startedAt,
    });
    return cached;
  }
  reportOpenLog("getReportById.start", {
    reportId,
    traceId: traceId || null,
    userId,
  });
  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_DETAIL_CACHE_TTL_MS,
    loader: async () => {
      const requestStartedAt = reportNow();
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : "",
          ...(traceId ? { "x-report-open-trace": traceId } : {}),
        },
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(
          `Get report by id failed (${res.status}): ${text || res.statusText}`
        );
      }

      try {
        const parsed = JSON.parse(text);
        reportOpenLog("getReportById.complete", {
          reportId,
          traceId: traceId || null,
          durationMs: reportNow() - requestStartedAt,
          totalMs: reportNow() - startedAt,
        });
        return parsed;
      } catch {
        throw new Error(`Failed to parse report by id response: "${text}"`);
      }
    },
  });
}

export async function getSignedReportAssetUrl({ key, authToken } = {}) {
  if (!key) throw new Error("key is required");

  const url = new URL(`${API_BASE}/reports/asset-url`);
  url.searchParams.set("key", key);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: authToken ? `Bearer ${authToken}` : "",
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `Get signed report asset url failed (${res.status}): ${text || res.statusText}`
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse signed report asset url response: "${text}"`);
  }
}

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export async function downloadReportPdf({ reportId, userId, authToken, filename } = {}) {
  if (!reportId) throw new Error("reportId is required");
  if (!userId) throw new Error("userId is required");

  const url = new URL(`${API_BASE}/reports/${reportId}/pdf`);
  url.searchParams.set("userId", userId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: authToken ? `Bearer ${authToken}` : "",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Report PDF request failed (${res.status}): ${text || res.statusText}`);
  }

  const blob = await res.blob();
  triggerBlobDownload(blob, filename || `report-${reportId}.pdf`);
}

export async function downloadBrandPdf({
  tab,
  userId,
  shopId,
  authToken,
  filename,
} = {}) {
  if (!tab) throw new Error("tab is required");
  if (!userId) throw new Error("userId is required");

  const url = new URL(`${API_BASE}/reports/brand/pdf`);
  url.searchParams.set("tab", tab);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: authToken ? `Bearer ${authToken}` : "",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Brand PDF request failed (${res.status}): ${text || res.statusText}`);
  }

  const blob = await res.blob();
  triggerBlobDownload(blob, filename || `${tab}.pdf`);
}

export async function createShopMonthlyIdeation({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request monthly ideation.");
  }

  const endpoint = `${API_BASE}/shop/create-monthly-ideation-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Monthly ideation request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createShopMetaReport({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request ads audit report.");
  }

  const endpoint = `${API_BASE}/shop/create-shop-meta-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Meta Report request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function analyzeShopMetaAdset({
  userId,
  reportId,
  shopId,
  adsetId,
  adsetName,
  force = false,
  authToken,
}) {
  if (!userId) {
    throw new Error("userId is required to analyze a Meta ad set.");
  }
  if (!adsetId && !adsetName) {
    throw new Error("adsetId or adsetName is required to analyze a Meta ad set.");
  }

  const endpoint = `${API_BASE}/shop/meta-adset-analysis`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const cacheKey = buildReportCacheKey("meta-adset-analysis", [
    userId,
    reportId || "",
    shopId || "",
    adsetId || "",
    adsetName || "",
  ]);
  const payload = {
    user_id: userId,
    report_id: reportId,
    shop_id: shopId,
    adset_id: adsetId,
    adset_name: adsetName,
    force,
  };

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Meta ad set analysis request failed (${res.status}): ${text || res.statusText}`
      );
    }

    const parsed = await parseJsonSafely(res);
    if (parsed?.ok) {
      writeReportCache(cacheKey, parsed, REPORT_DETAIL_CACHE_TTL_MS);
    }
    return parsed;
  } catch (error) {
    if (!isLikelyFetchDisconnect(error)) throw error;
    const recovered = await recoverAnalysisAfterDisconnect({
      loadSavedAnalysis: () =>
        getShopMetaAdsetAnalysis({
          userId,
          reportId,
          shopId,
          adsetId,
          adsetName,
          authToken,
        }),
    });
    writeReportCache(cacheKey, recovered, REPORT_DETAIL_CACHE_TTL_MS);
    return recovered;
  }
}

export async function analyzeShopMetaCampaign({
  userId,
  reportId,
  shopId,
  campaignId,
  campaignName,
  fromMonth,
  toMonth,
  force = false,
  authToken,
}) {
  if (!userId) {
    throw new Error("userId is required to analyze a Meta campaign.");
  }
  if (!campaignId && !campaignName) {
    throw new Error("campaignId or campaignName is required to analyze a Meta campaign.");
  }

  const endpoint = `${API_BASE}/shop/meta-campaign-analysis`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const cacheKey = buildReportCacheKey("meta-campaign-analysis", [
    userId,
    reportId || "",
    shopId || "",
    campaignId || "",
    campaignName || "",
    fromMonth || "",
    toMonth || "",
  ]);
  const payload = {
    user_id: userId,
    report_id: reportId,
    shop_id: shopId,
    campaign_id: campaignId,
    campaign_name: campaignName,
    from_month: fromMonth,
    to_month: toMonth,
    force,
  };

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Meta campaign analysis request failed (${res.status}): ${text || res.statusText}`
      );
    }

    const parsed = await parseJsonSafely(res);
    if (parsed?.ok) {
      writeReportCache(cacheKey, parsed, REPORT_DETAIL_CACHE_TTL_MS);
    }
    return parsed;
  } catch (error) {
    if (!isLikelyFetchDisconnect(error)) throw error;
    const recovered = await recoverAnalysisAfterDisconnect({
      loadSavedAnalysis: () =>
        getShopMetaCampaignAnalysis({
          userId,
          reportId,
          shopId,
          campaignId,
          campaignName,
          fromMonth,
          toMonth,
          authToken,
        }),
    });
    writeReportCache(cacheKey, recovered, REPORT_DETAIL_CACHE_TTL_MS);
    return recovered;
  }
}

export async function getShopMetaCampaignAnalysis({
  userId,
  reportId,
  shopId,
  campaignId,
  campaignName,
  fromMonth,
  toMonth,
  authToken,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to retrieve a Meta campaign analysis.");
  }
  if (!campaignId && !campaignName) {
    throw new Error("campaignId or campaignName is required to retrieve a Meta campaign analysis.");
  }

  const url = new URL(`${API_BASE}/shop/meta-campaign-analysis`);
  url.searchParams.set("userId", userId);
  if (reportId) url.searchParams.set("reportId", reportId);
  if (shopId) url.searchParams.set("shopId", shopId);
  if (campaignId) url.searchParams.set("campaignId", campaignId);
  if (campaignName) url.searchParams.set("campaignName", campaignName);
  if (fromMonth) url.searchParams.set("fromMonth", fromMonth);
  if (toMonth) url.searchParams.set("toMonth", toMonth);

  const cacheKey = buildReportCacheKey("meta-campaign-analysis", [
    userId,
    reportId || "",
    shopId || "",
    campaignId || "",
    campaignName || "",
    fromMonth || "",
    toMonth || "",
  ]);

  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_DETAIL_CACHE_TTL_MS,
    shouldCache: hasAssessmentPayload,
    loader: async () => {
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      });

      const text = await res.text().catch(() => "");
      if (!res.ok) {
        throw new Error(
          `Meta campaign analysis retrieval failed (${res.status}): ${text || res.statusText}`
        );
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Failed to parse meta campaign analysis response: "${text}"`);
      }
    },
  });
}

export async function getShopMetaAdsetAnalysis({
  userId,
  reportId,
  shopId,
  adsetId,
  adsetName,
  authToken,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to retrieve a Meta ad set analysis.");
  }
  if (!adsetId && !adsetName) {
    throw new Error("adsetId or adsetName is required to retrieve a Meta ad set analysis.");
  }

  const url = new URL(`${API_BASE}/shop/meta-adset-analysis`);
  url.searchParams.set("userId", userId);
  if (reportId) url.searchParams.set("reportId", reportId);
  if (shopId) url.searchParams.set("shopId", shopId);
  if (adsetId) url.searchParams.set("adsetId", adsetId);
  if (adsetName) url.searchParams.set("adsetName", adsetName);

  const cacheKey = buildReportCacheKey("meta-adset-analysis", [
    userId,
    reportId || "",
    shopId || "",
    adsetId || "",
    adsetName || "",
  ]);

  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_DETAIL_CACHE_TTL_MS,
    shouldCache: hasAssessmentPayload,
    loader: async () => {
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      });

      const text = await res.text().catch(() => "");
      if (!res.ok) {
        throw new Error(
          `Meta ad set analysis retrieval failed (${res.status}): ${text || res.statusText}`
        );
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Failed to parse meta ad set analysis response: "${text}"`);
      }
    },
  });
}

export async function analyzeShopMetaAd({
  userId,
  reportId,
  shopId,
  adId,
  adName,
  force = false,
  authToken,
}) {
  if (!userId) {
    throw new Error("userId is required to analyze a Meta ad.");
  }
  if (!adId && !adName) {
    throw new Error("adId or adName is required to analyze a Meta ad.");
  }

  const endpoint = `${API_BASE}/shop/meta-ad-analysis`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const cacheKey = buildReportCacheKey("meta-ad-analysis", [
    userId,
    reportId || "",
    shopId || "",
    adId || "",
    adName || "",
  ]);
  const payload = {
    user_id: userId,
    report_id: reportId,
    shop_id: shopId,
    ad_id: adId,
    ad_name: adName,
    force,
  };

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Meta ad analysis request failed (${res.status}): ${text || res.statusText}`
      );
    }

    const parsed = await parseJsonSafely(res);
    if (parsed?.ok) {
      writeReportCache(cacheKey, parsed, REPORT_DETAIL_CACHE_TTL_MS);
    }
    return parsed;
  } catch (error) {
    if (!isLikelyFetchDisconnect(error)) throw error;
    const recovered = await recoverAnalysisAfterDisconnect({
      loadSavedAnalysis: () =>
        getShopMetaAdAnalysis({
          userId,
          reportId,
          shopId,
          adId,
          adName,
          authToken,
        }),
    });
    writeReportCache(cacheKey, recovered, REPORT_DETAIL_CACHE_TTL_MS);
    return recovered;
  }
}

export async function getShopMetaAdAnalysis({
  userId,
  reportId,
  shopId,
  adId,
  adName,
  authToken,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to retrieve a Meta ad analysis.");
  }
  if (!adId && !adName) {
    throw new Error("adId or adName is required to retrieve a Meta ad analysis.");
  }

  const url = new URL(`${API_BASE}/shop/meta-ad-analysis`);
  url.searchParams.set("userId", userId);
  if (reportId) url.searchParams.set("reportId", reportId);
  if (shopId) url.searchParams.set("shopId", shopId);
  if (adId) url.searchParams.set("adId", adId);
  if (adName) url.searchParams.set("adName", adName);

  const cacheKey = buildReportCacheKey("meta-ad-analysis", [
    userId,
    reportId || "",
    shopId || "",
    adId || "",
    adName || "",
  ]);

  return getCachedReportJson({
    key: cacheKey,
    ttlMs: REPORT_DETAIL_CACHE_TTL_MS,
    shouldCache: hasAssessmentPayload,
    loader: async () => {
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      });

      const text = await res.text().catch(() => "");
      if (!res.ok) {
        throw new Error(
          `Meta ad analysis retrieval failed (${res.status}): ${text || res.statusText}`
        );
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Failed to parse meta ad analysis response: "${text}"`);
      }
    },
  });
}


export async function createShopMarketAnalysis({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request market analysis report.");
  }

  const endpoint = `${API_BASE}/storeleads/athena/query-with-url-matching`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Market Analysis request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function fetchStoreLeadMarketCompanyList({
  runKey,
  reportType = "SOM",
  listType,
  countryCode,
  page = 1,
  limit = 100,
  authToken,
} = {}) {
  if (!runKey || !listType) {
    throw new Error("runKey and listType are required to fetch StoreLeads company list.");
  }

  const url = new URL(`${API_BASE}/storeleads/market-company-list`);
  url.searchParams.set("runKey", runKey);
  url.searchParams.set("reportType", reportType);
  url.searchParams.set("listType", listType);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (countryCode) url.searchParams.set("countryCode", countryCode);

  const headers = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(url.toString(), { method: "GET", headers });
  const text = await res.text().catch(() => "");
  if (!res.ok) {
    throw new Error(
      `StoreLeads company list failed (${res.status}): ${text || res.statusText}`
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse StoreLeads company list response: "${text}"`);
  }
}

export async function createShopTargetAudienceReport({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request target audience report.");
  }

  const endpoint = `${API_BASE}/shop/create-target-audience-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Target audience report request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createCompetitorPricingReport({ userId, shopId, authToken }) {
  if (!userId) throw new Error("userId is required for competitor pricing report.");
  const endpoint = `${API_BASE}/shop/create-competitor-pricing-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ userId, shopId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Competitor pricing request failed (${res.status}): ${text || res.statusText}`);
  }
  return parseJsonSafely(res);
}

export async function createShopCompetitorAnalysis({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request competitor analysis report.");
  }

  const endpoint = `${API_BASE}/shop/create-competitor-analysis-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Competitor Analysis request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createShopAdsAuditReport({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request ads audit report.");
  }

  const endpoint = `${API_BASE}/shop/create-ads-audit-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Ads Audit request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createShopInfluencerContentReport({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request influencer content report.");
  }

  const endpoint = `${API_BASE}/shop/create-influencer-content-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Influencer content request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createShopSeoReport({ userId, authToken }) {
  if (!userId) {
    throw new Error("userId is required to request shop SEO report.");
  }

  const endpoint = `${API_BASE}/shop/create-shop-seo-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Shop SEO report request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function createProductPricingReport({
  userId,
  productIds,
  limit,
  authToken,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to request the pricing report.");
  }

  const endpoint = `${API_BASE}/shopify/pricing-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const body = { user_id: userId };
  if (Array.isArray(productIds) && productIds.length) {
    body.product_ids = productIds;
  }
  if (limit) body.limit = limit;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Product pricing report request failed (${res.status}): ${
        text || res.statusText
      }`
    );
  }

  return parseJsonSafely(res);
}

export async function createShopPriceAnalysis({
  userId,
  authToken,
  productIds,
  limit,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to request price analysis.");
  }

  const endpoint = `${API_BASE}/shopify/price-analysis`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const body = {
    user_id: userId,
  };
  if (Array.isArray(productIds) && productIds.length) {
    body.product_ids = productIds.map(String);
  }
  if (typeof limit === "number") body.limit = limit;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Price analysis request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}


export async function createShopProfileReport({ userId, shopId, tabKey, authToken, extraBody = {} } = {}) {
  if (!userId) throw new Error("userId is required.");

  const ENDPOINT_BY_TAB = {
    "main-information": "/shop/create-shop-profile-main-information",
    "geography-market-coverage": "/shop/create-shop-profile-geography-market-coverage",
    "product-catalog-structure": "/shop/create-shop-profile-product-catalog-structure",
    "pricing-commercial-position": "/shop/create-shop-profile-pricing-commercial-position",
    "target-audience": "/shop/create-shop-profile-target-audience",
    "brand-positioning": "/shop/create-shop-profile-brand-positioning",
    "keywords-market-language": "/shop/create-shop-keywords-market-language",
  };

  const path = ENDPOINT_BY_TAB[tabKey];
  if (!path) throw new Error(`Unknown store profile tab key: ${tabKey}`);

  const endpoint = `${API_BASE}${path}`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId, ...(shopId ? { shop_id: shopId } : {}), ...extraBody }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Store profile report request failed (${res.status}): ${text || res.statusText}`);
  }
  return parseJsonSafely(res);
}

export async function createShopYearlyContentReport({
  userId,
  authToken,
  year,
  strategyId,
} = {}) {
  if (!userId) {
    throw new Error("userId is required to request yearly content report.");
  }

  const endpoint = `${API_BASE}/shop/create-yearly-content-report`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const body = {
    user_id: userId,
  };
  if (typeof year === "number") body.year = year;
  if (strategyId) body.strategy_id = strategyId;

  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Yearly Content Report request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

// Short TTL for live data — 2 minutes so the same window doesn't re-fetch constantly
const LIVE_INSIGHTS_CACHE_TTL_MS = 2 * 60 * 1000;

export async function getMetaLiveInsights({
  userId,
  reportId,
  shopId,
  since,
  until,
  authToken,
} = {}) {
  if (!userId) throw new Error("userId is required for live Meta insights.");
  if (!since || !until) throw new Error("since and until are required for live Meta insights.");

  const cacheKey = buildReportCacheKey("meta-live-insights", [
    userId,
    reportId || "",
    shopId || "",
    since,
    until,
  ]);

  const cached = readReportCache(cacheKey);
  if (cached) return cached;

  const url = new URL(`${API_BASE}/shop/meta-live-insights`);
  url.searchParams.set("userId", userId);
  if (reportId) url.searchParams.set("reportId", reportId);
  if (shopId) url.searchParams.set("shopId", shopId);
  url.searchParams.set("since", since);
  url.searchParams.set("until", until);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Live Meta insights request failed (${res.status}): ${text || res.statusText}`);
  }

  const parsed = await parseJsonSafely(res);
  if (parsed?.ok) {
    writeReportCache(cacheKey, parsed, LIVE_INSIGHTS_CACHE_TTL_MS);
  }
  return parsed;
}

/**
 * Force-refreshes a report, deducting 1 credit from the subscription.
 * Returns { ok, creditsRemaining } on success or { ok: false, code, message } on error.
 *
 * @param {{ reportKey: string, userId: string, shopId: string, authToken: string }} params
 */
export async function forceRefreshReport({ reportKey, userId, shopId, authToken }) {
  const res = await fetch(`${API_BASE}/reports/force-refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ reportKey, userId, shopId }),
  });
  const data = await parseJsonSafely(res);
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.message || `Force refresh failed (${res.status})`);
    err.code = data?.code || "FORCE_REFRESH_FAILED";
    err.creditsRemaining = data?.creditsRemaining ?? null;
    throw err;
  }
  return data;
}

export async function createBrandIntelligenceReport({ userId, shopId, authToken } = {}) {
  if (!userId) throw new Error("userId is required.");
  const res = await fetch(`${API_BASE}/shop/create-brand-intelligence-report`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ userId, shopId }),
  });
  const json = await parseJsonSafely(res);
  if (!res.ok || json?.ok === false) {
    throw new Error(json?.message || json?.error || "Failed to create brand intelligence report");
  }
  return json;
}
